import { Alert, Box, Tab, Tabs } from '@mui/material';

import { CustomButton, FormTitleBox, MaterialReportTable } from '../../common/components';
import MaterialColumnFilters from './components/MaterialColumnFilters';
import MaterialDashboardCharts from './components/MaterialDashboardCharts';
import MaterialDashboardFilters from './components/MaterialDashboardFilters';
import MaterialDashboardIndicators from './components/MaterialDashboardIndicators';
import MaterialStageChart from './components/MaterialStageChart';
import {
  DASHBOARD_TAB_KIND,
  DASHBOARD_TABS,
  MATERIAL_TEXTS,
} from './constants/materialDashboardConstants';
import { useMaterialDashboard } from './hooks/useMaterialDashboard';
import { buildDashboardInfoLines } from './utils/buildDashboardInfoLines';

const tabsSx = {
  minHeight: 44,
  '& .MuiTab-root': {
    minHeight: 44,
    fontSize: 13,
    fontWeight: 700,
  },
};

const MaterialDashboard = () => {
  const {
    activeTab,
    changeColumnFilters,
    changePeriod,
    clear,
    columnFilters,
    drillProject,
    drillProvider,
    errorMessage,
    filters,
    groups,
    handleFilterChange,
    handleRowsPerPageChange,
    rowsPerPage,
    isRefreshing,
    isSummaryLoading,
    page,
    period,
    providers,
    refresh,
    retry,
    scopeOptions,
    selectRow,
    selectTab,
    setPage,
    sort,
    sortTable,
    summary,
    tabIndex,
    table,
    tableColumns,
    warehouses,
  } = useMaterialDashboard();

  return (
    <Box>
      <FormTitleBox title={MATERIAL_TEXTS.title} />

      {errorMessage && (
        <Alert
          severity="error"
          action={(
            <CustomButton variant="outlined" size="small" onClick={retry}>
              {MATERIAL_TEXTS.retry}
            </CustomButton>
          )}
        >
          {errorMessage}
        </Alert>
      )}

      <MaterialDashboardFilters
        filters={filters}
        period={period}
        scopeOptions={scopeOptions}
        warehouses={warehouses}
        groups={groups}
        providers={providers}
        infoLines={buildDashboardInfoLines(summary)}
        hasWarnings={Boolean(summary?.warnings?.length)}
        onChange={handleFilterChange}
        onPeriodChange={changePeriod}
        onClear={clear}
        onRefresh={refresh}
        isRefreshing={isRefreshing}
      />

      <MaterialDashboardIndicators totals={summary?.totals} isLoading={isSummaryLoading} />

      <Box sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, value) => selectTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={tabsSx}
        >
          {DASHBOARD_TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {activeTab.kind === DASHBOARD_TAB_KIND.summary && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            p: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <MaterialStageChart totals={summary?.totals} isLoading={isSummaryLoading} />
          <MaterialDashboardCharts
            projects={summary?.projects}
            providers={summary?.providers}
            isLoading={isSummaryLoading}
            onProject={drillProject}
            onProvider={drillProvider}
          />
        </Box>
      )}

      {activeTab.kind === DASHBOARD_TAB_KIND.report && (
        <MaterialColumnFilters
          key={activeTab.value}
          options={table.columnOptions}
          rows={table.rows}
          value={columnFilters}
          onChange={changeColumnFilters}
          allowedColumns={tableColumns}
        />
      )}

      {activeTab.kind !== DASHBOARD_TAB_KIND.summary && (
        <MaterialReportTable
          rows={table.rows}
          columns={tableColumns}
          totals={table.totals}
          total={table.total}
          page={page}
          onPage={setPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSelect={selectRow}
          isLoading={table.isLoading}
          sort={sort}
          onSort={sortTable}
        />
      )}
    </Box>
  );
};

export default MaterialDashboard;
